/**
 * Imperative Skia draw primitives shared by every built-in renderer.
 *
 * Perf notes (record-time only — none of this runs per frame):
 * - Paints are allocated per draw call. At 1000 objects that is ~2k short-
 *   lived paints per RE-RECORD (a committed edit), which profiles far below
 *   the reconciliation cost this architecture avoids. A paint pool is the
 *   documented next lever if record time ever shows up in traces.
 * - Rotation uses `canvas.save() → rotate(deg, cx, cy) → restore()` so the
 *   object's local math stays axis-aligned.
 * - LOD contract: `FAR` = fill only (no stroke/text), `MEDIUM`+ = stroke +
 *   label, `CLOSE` reserved for metadata badges (drawn by CUSTOM renderers —
 *   the engine built-ins never interpret `metadata`).
 *
 * @module floorplan/renderer/renderers/draw
 * @category FloorPlan
 */

import type { SkCanvas, SkPaint } from '@shopify/react-native-skia';
import { BlurStyle, PaintStyle, Skia } from '@shopify/react-native-skia';

import { LOD } from '../../models/camera';
import type { FloorObject, ObjectStyle } from '../../models/object';
import { effectiveSize, objectCenter } from '../../utils/bounds';
import type { RenderContext } from '../types';

const DEFAULT_STROKE_WIDTH = 1.5;
const DEFAULT_FONT_SIZE = 14;

const fillPaint = (color: string, opacity: number): SkPaint => {
  const paint = Skia.Paint();
  paint.setAntiAlias(true);
  paint.setColor(Skia.Color(color));
  paint.setStyle(PaintStyle.Fill);
  if (opacity < 1) paint.setAlphaf(opacity);
  return paint;
};

const strokePaint = (
  color: string,
  width: number,
  opacity: number,
): SkPaint => {
  const paint = Skia.Paint();
  paint.setAntiAlias(true);
  paint.setColor(Skia.Color(color));
  paint.setStyle(PaintStyle.Stroke);
  paint.setStrokeWidth(width);
  if (opacity < 1) paint.setAlphaf(opacity);
  return paint;
};

/**
 * Relative-luminance contrast pick: dark ink on light fills, light ink on
 * dark fills — keeps labels readable regardless of the domain's palette.
 * Non-hex strings fall through to the provided fallback.
 */
const contrastLabelColor = (fill: string | null, fallback: string): string => {
  if (fill === null || !fill.startsWith('#')) return fallback;
  let hex = fill.slice(1);
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (hex.length < 6) return fallback;
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return fallback;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  if (luminance > 0.6) return '#1c2b22';
  return '#f6faf7';
};

/** Soft elevation shadow under a rounded rect (MEDIUM+ only). */
const drawSoftShadow = (
  canvas: SkCanvas,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  opacity: number,
): void => {
  const paint = Skia.Paint();
  paint.setAntiAlias(true);
  paint.setColor(Skia.Color('#000000'));
  paint.setAlphaf(0.28 * opacity);
  paint.setMaskFilter(Skia.MaskFilter.MakeBlur(BlurStyle.Normal, 4, true));
  const rect = Skia.XYWHRect(x, y + 2.5, width, height);
  canvas.drawRRect(Skia.RRectXY(rect, radius, radius), paint);
};

/** Seat-stub count clamp — keeps a corrupt document from drawing hundreds. */
const MAX_SEAT_STUBS = 12;

/**
 * Chair stubs along the top/bottom edges (style.seats). Drawn UNDER the
 * body so the table overlaps their inner half — the classic seating-plan
 * silhouette.
 */
const drawSeatStubs = (
  canvas: SkCanvas,
  object: FloorObject,
  seats: number,
  color: string,
  opacity: number,
): void => {
  const count = Math.min(Math.max(Math.floor(seats), 0), MAX_SEAT_STUBS);
  if (count === 0) return;
  const size = effectiveSize(object);
  const stubW = 16;
  const stubH = 8;
  const gapFromEdge = 3;
  const topCount = Math.ceil(count / 2);
  const bottomCount = count - topCount;
  const paint = Skia.Paint();
  paint.setAntiAlias(true);
  paint.setColor(Skia.Color(color));
  paint.setAlphaf(0.9 * opacity);
  const drawRow = (rowCount: number, yPos: number): void => {
    for (let i = 0; i < rowCount; i += 1) {
      const cx = object.x + ((i + 1) * size.width) / (rowCount + 1);
      const rect = Skia.XYWHRect(cx - stubW / 2, yPos, stubW, stubH);
      canvas.drawRRect(Skia.RRectXY(rect, 3, 3), paint);
    }
  };
  drawRow(topCount, object.y - stubH + gapFromEdge);
  drawRow(bottomCount, object.y + size.height - gapFromEdge);
};

/** Status-badge dot at the top-right corner (style.badge). */
const drawBadgeDot = (
  canvas: SkCanvas,
  object: FloorObject,
  color: string,
  opacity: number,
): void => {
  const size = effectiveSize(object);
  const cx = object.x + size.width - 9;
  const cy = object.y + 9;
  canvas.drawCircle(cx, cy, 5, fillPaint(color, opacity));
  canvas.drawCircle(cx, cy, 5, strokePaint('#ffffff', 1.5, 0.9 * opacity));
};

/** Visual defaults a specific renderer wants when the object has no style. */
export type DrawDefaults = {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
  /** Draw the label at MEDIUM+ (when `style.text` is present). */
  showLabel?: boolean;
  /** Fill the shape at all (rooms are stroke-only). */
  filled?: boolean;
  /** Dashed outline (rooms/zones — the architectural boundary style). */
  dashed?: boolean;
};

const resolveStyle = (
  object: FloorObject,
  defaults: DrawDefaults,
  ctx: RenderContext,
): Required<Pick<ObjectStyle, 'opacity'>> & {
  fill: string | null;
  stroke: string | null;
  strokeWidth: number;
  cornerRadius: number;
  text: string | null;
  subLabel: string | null;
  textColor: string;
  fontSize: number;
  showLabel: boolean;
} => {
  const style = object.style;
  const filled = defaults.filled ?? true;
  const fillColor = style?.fill ?? defaults.fill ?? ctx.theme.objectFill;
  const strokeColor =
    style?.stroke ?? defaults.stroke ?? ctx.theme.objectStroke;
  return {
    opacity: style?.opacity ?? 1,
    fill: filled ? fillColor : null,
    stroke: strokeColor,
    strokeWidth:
      style?.strokeWidth ?? defaults.strokeWidth ?? DEFAULT_STROKE_WIDTH,
    cornerRadius: style?.cornerRadius ?? defaults.cornerRadius ?? 0,
    text: style?.text ?? null,
    subLabel: style?.subLabel ?? null,
    textColor: style?.textColor ?? ctx.theme.label,
    fontSize: style?.fontSize ?? DEFAULT_FONT_SIZE,
    showLabel: defaults.showLabel ?? true,
  };
};

/** Apply the object's rotation to the canvas (caller must save/restore). */
const applyRotation = (canvas: SkCanvas, object: FloorObject): void => {
  if (object.rotation === 0) return;
  const center = objectCenter(object);
  canvas.rotate(object.rotation, center.x, center.y);
};

/** Centered label, drawn at MEDIUM+ when a font is available. */
const drawLabel = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
  text: string,
  color: string,
  fontSize: number,
): void => {
  const font = ctx.font;
  if (font === null) return;
  font.setSize(fontSize);
  // getTextWidth (glyph-width sum), NOT measureText — measureText is
  // unimplemented on React Native Web and throws at draw time.
  const width = font.getTextWidth(text);
  const center = objectCenter(object);
  const paint = fillPaint(color, 1);
  // Text height is glyph-bound; centering on ~0.35em is the standard
  // optical baseline correction for a single centered line.
  canvas.drawText(
    text,
    center.x - width / 2,
    center.y + fontSize * 0.35,
    paint,
    font,
  );
};

/**
 * Secondary label (style.subLabel), drawn under the primary one at a smaller
 * size. Generic — the POS binding sets it to the occupied table's order #.
 */
const drawSubLabel = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
  text: string,
  color: string,
  primaryFontSize: number,
): void => {
  const font = ctx.font;
  if (font === null) return;
  const size = primaryFontSize * 0.78;
  font.setSize(size);
  const width = font.getTextWidth(text);
  const center = objectCenter(object);
  const paint = fillPaint(color, 0.85);
  canvas.drawText(
    text,
    center.x - width / 2,
    center.y + primaryFontSize * 0.35 + size * 1.15,
    paint,
    font,
  );
};

/** Rect-family drawer (rect, table, chair, wall, counter, room, door, …). */
export const drawRectLike = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
  defaults: DrawDefaults = {},
): void => {
  const style = resolveStyle(object, defaults, ctx);
  const size = effectiveSize(object);
  const rect = Skia.XYWHRect(object.x, object.y, size.width, size.height);
  const detailed = ctx.lod !== LOD.FAR;
  const seats = object.style?.seats ?? 0;
  const badge = object.style?.badge ?? null;

  canvas.save();
  applyRotation(canvas, object);
  // Depth pass (MEDIUM+): seat stubs sit UNDER the body; a soft shadow
  // lifts the object off the grid.
  if (detailed && seats > 0 && style.stroke !== null) {
    drawSeatStubs(canvas, object, seats, style.stroke, style.opacity);
  }
  if (detailed && style.fill !== null) {
    drawSoftShadow(
      canvas,
      object.x,
      object.y,
      size.width,
      size.height,
      style.cornerRadius,
      style.opacity,
    );
  }
  if (style.fill !== null) {
    const paint = fillPaint(style.fill, style.opacity);
    if (style.cornerRadius > 0) {
      canvas.drawRRect(
        Skia.RRectXY(rect, style.cornerRadius, style.cornerRadius),
        paint,
      );
    } else {
      canvas.drawRect(rect, paint);
    }
  }
  // Inner top-light highlight — the subtle inset that reads as a surface.
  if (detailed && style.fill !== null && size.width > 24 && size.height > 24) {
    const inset = Skia.XYWHRect(
      object.x + 3,
      object.y + 3,
      size.width - 6,
      size.height - 6,
    );
    const innerRadius = Math.max(style.cornerRadius - 2, 0);
    canvas.drawRRect(
      Skia.RRectXY(inset, innerRadius, innerRadius),
      strokePaint('#ffffff', 1.2, 0.45 * style.opacity),
    );
  }
  if (detailed && style.stroke !== null) {
    const paint = strokePaint(style.stroke, style.strokeWidth, style.opacity);
    if (defaults.dashed === true) {
      paint.setPathEffect(Skia.PathEffect.MakeDash([10, 7], 0));
    }
    if (style.cornerRadius > 0) {
      canvas.drawRRect(
        Skia.RRectXY(rect, style.cornerRadius, style.cornerRadius),
        paint,
      );
    } else {
      canvas.drawRect(rect, paint);
    }
  }
  if (detailed && badge !== null) {
    drawBadgeDot(canvas, object, badge, style.opacity);
  }
  if (
    detailed &&
    style.showLabel &&
    style.text !== null &&
    style.text.length > 0
  ) {
    // Auto-contrast against the fill unless the document pinned a color.
    const labelColor =
      object.style?.textColor === undefined
        ? contrastLabelColor(style.fill, style.textColor)
        : style.textColor;
    drawLabel(canvas, object, ctx, style.text, labelColor, style.fontSize);
    if (style.subLabel !== null && style.subLabel.length > 0) {
      drawSubLabel(
        canvas,
        object,
        ctx,
        style.subLabel,
        labelColor,
        style.fontSize,
      );
    }
  }
  canvas.restore();
};

/** Ellipse inscribed in the object frame (circle type + round tables). */
export const drawEllipse = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
  defaults: DrawDefaults = {},
): void => {
  const style = resolveStyle(object, defaults, ctx);
  const size = effectiveSize(object);
  const rect = Skia.XYWHRect(object.x, object.y, size.width, size.height);

  canvas.save();
  applyRotation(canvas, object);
  if (style.fill !== null) {
    canvas.drawOval(rect, fillPaint(style.fill, style.opacity));
  }
  if (ctx.lod !== LOD.FAR && style.stroke !== null) {
    canvas.drawOval(
      rect,
      strokePaint(style.stroke, style.strokeWidth, style.opacity),
    );
  }
  if (
    ctx.lod !== LOD.FAR &&
    style.showLabel &&
    style.text !== null &&
    style.text.length > 0
  ) {
    drawLabel(canvas, object, ctx, style.text, style.textColor, style.fontSize);
    if (style.subLabel !== null && style.subLabel.length > 0) {
      drawSubLabel(
        canvas,
        object,
        ctx,
        style.subLabel,
        style.textColor,
        style.fontSize,
      );
    }
  }
  canvas.restore();
};

/** Polygon (closed) / polyline (open) from `object.points`. */
export const drawPolygonLike = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
  options: { close: boolean },
  defaults: DrawDefaults = {},
): void => {
  const points = object.points;
  if (points === undefined || points.length < 2) return;
  const style = resolveStyle(object, defaults, ctx);

  // PathBuilder, not the deprecated SkPath mutation API (removal announced).
  const builder = Skia.PathBuilder.Make();
  const first = points[0];
  if (first === undefined) return;
  // Points are relative to the object origin.
  builder.moveTo(object.x + first.x, object.y + first.y);
  for (let i = 1; i < points.length; i += 1) {
    const point = points[i];
    if (point === undefined) continue;
    builder.lineTo(object.x + point.x, object.y + point.y);
  }
  if (options.close) builder.close();
  const path = builder.detach();

  canvas.save();
  applyRotation(canvas, object);
  if (options.close && style.fill !== null) {
    canvas.drawPath(path, fillPaint(style.fill, style.opacity));
  }
  if (style.stroke !== null && (ctx.lod !== LOD.FAR || !options.close)) {
    canvas.drawPath(
      path,
      strokePaint(style.stroke, style.strokeWidth, style.opacity),
    );
  }
  canvas.restore();
};

/** Standalone text object — the label IS the object. */
export const drawTextObject = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
): void => {
  const style = resolveStyle(object, { showLabel: true, filled: false }, ctx);
  if (ctx.lod === LOD.FAR) return;
  if (style.text === null || style.text.length === 0) return;
  canvas.save();
  applyRotation(canvas, object);
  drawLabel(canvas, object, ctx, style.text, style.textColor, style.fontSize);
  canvas.restore();
};

/**
 * Architectural DOOR symbol: jamb ticks at both sides of the opening, the
 * door leaf standing open, and the quarter-circle swing arc — the universal
 * plan notation, so a door never reads as "another rectangle".
 */
export const drawDoor = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
): void => {
  const style = resolveStyle(object, { showLabel: false }, ctx);
  const size = effectiveSize(object);
  const stroke = style.stroke ?? '#94a3b8';
  const radius = Math.min(size.width, size.height * 2);

  canvas.save();
  applyRotation(canvas, object);
  const baseY = object.y + size.height;
  // Jamb ticks.
  const jamb = fillPaint(stroke, style.opacity);
  canvas.drawRect(Skia.XYWHRect(object.x - 2, baseY - 6, 4, 6), jamb);
  canvas.drawRect(
    Skia.XYWHRect(object.x + size.width - 2, baseY - 6, 4, 6),
    jamb,
  );
  if (ctx.lod !== LOD.FAR) {
    const line = strokePaint(stroke, 2, style.opacity);
    // Leaf standing open (hinge at the left jamb).
    canvas.drawLine(object.x, baseY, object.x, baseY - radius, line);
    // Swing arc from leaf tip to the far jamb.
    const oval = Skia.XYWHRect(
      object.x - radius,
      baseY - radius,
      radius * 2,
      radius * 2,
    );
    canvas.drawArc(oval, -90, 90, false, line);
  }
  canvas.restore();
};

/**
 * Architectural WINDOW symbol: thin frame with a mullion line along the
 * long axis (the classic double-line wall opening).
 */
export const drawWindow = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
): void => {
  const style = resolveStyle(
    { ...object, style: { cornerRadius: 0, ...object.style } },
    { showLabel: false, strokeWidth: 2 },
    ctx,
  );
  const size = effectiveSize(object);
  const stroke = style.stroke ?? '#94a3b8';
  const rect = Skia.XYWHRect(object.x, object.y, size.width, size.height);

  canvas.save();
  applyRotation(canvas, object);
  if (style.fill !== null) {
    canvas.drawRect(rect, fillPaint(style.fill, 0.35 * style.opacity));
  }
  const line = strokePaint(stroke, style.strokeWidth, style.opacity);
  canvas.drawRect(rect, line);
  if (ctx.lod !== LOD.FAR) {
    if (size.width >= size.height) {
      const midY = object.y + size.height / 2;
      canvas.drawLine(object.x, midY, object.x + size.width, midY, line);
    } else {
      const midX = object.x + size.width / 2;
      canvas.drawLine(midX, object.y, midX, object.y + size.height, line);
    }
  }
  canvas.restore();
};

/**
 * CHAIR symbol: rounded seat with a thicker backrest bar along the top
 * edge — instantly separable from tables at a glance.
 */
export const drawChair = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
): void => {
  const style = resolveStyle(
    object,
    { showLabel: false, cornerRadius: 6 },
    ctx,
  );
  const size = effectiveSize(object);
  const stroke = style.stroke ?? '#94a3b8';
  const backH = Math.max(Math.min(size.height * 0.22, 10), 4);

  canvas.save();
  applyRotation(canvas, object);
  // Seat.
  const seat = Skia.XYWHRect(
    object.x,
    object.y + backH * 0.6,
    size.width,
    size.height - backH * 0.6,
  );
  if (style.fill !== null) {
    canvas.drawRRect(
      Skia.RRectXY(seat, style.cornerRadius, style.cornerRadius),
      fillPaint(style.fill, style.opacity),
    );
  }
  if (ctx.lod !== LOD.FAR) {
    canvas.drawRRect(
      Skia.RRectXY(seat, style.cornerRadius, style.cornerRadius),
      strokePaint(stroke, 1.5, style.opacity),
    );
    // Backrest bar.
    const back = Skia.XYWHRect(
      object.x + size.width * 0.12,
      object.y,
      size.width * 0.76,
      backH,
    );
    canvas.drawRRect(
      Skia.RRectXY(back, 3, 3),
      fillPaint(stroke, style.opacity),
    );
  }
  canvas.restore();
};

/**
 * COUNTER symbol: solid worktop with a "front edge" line inset along the
 * long axis — the bar/servery notation.
 */
export const drawCounter = (
  canvas: SkCanvas,
  object: FloorObject,
  ctx: RenderContext,
): void => {
  const style = resolveStyle(object, { cornerRadius: 3 }, ctx);
  const size = effectiveSize(object);
  const stroke = style.stroke ?? '#94a3b8';
  const rect = Skia.XYWHRect(object.x, object.y, size.width, size.height);

  canvas.save();
  applyRotation(canvas, object);
  if (style.fill !== null) {
    canvas.drawRRect(
      Skia.RRectXY(rect, style.cornerRadius, style.cornerRadius),
      fillPaint(style.fill, style.opacity),
    );
  }
  if (ctx.lod !== LOD.FAR) {
    canvas.drawRRect(
      Skia.RRectXY(rect, style.cornerRadius, style.cornerRadius),
      strokePaint(stroke, style.strokeWidth, style.opacity),
    );
    // Front-edge line, 70% toward the customer side of the long axis.
    const line = strokePaint(stroke, 1.5, 0.8 * style.opacity);
    if (size.width >= size.height) {
      const edgeY = object.y + size.height * 0.72;
      canvas.drawLine(
        object.x + 3,
        edgeY,
        object.x + size.width - 3,
        edgeY,
        line,
      );
    } else {
      const edgeX = object.x + size.width * 0.72;
      canvas.drawLine(
        edgeX,
        object.y + 3,
        edgeX,
        object.y + size.height - 3,
        line,
      );
    }
    if (style.showLabel && style.text !== null && style.text.length > 0) {
      drawLabel(
        canvas,
        object,
        ctx,
        style.text,
        contrastLabelColor(style.fill, style.textColor),
        style.fontSize,
      );
    }
  }
  canvas.restore();
};
